import { makeAutoObservable, observable, runInAction } from 'mobx'

import { loadPdfJs } from '../Helpers/diskPdf'

import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist'

/**
 * `auto` — за замовчуванням: по ширині, але не більше {@link PDF_AUTO_MAX}
 * (на широкому моніторі «по ширині» дає 230 % і сторінку не охопити оком);
 * `width` — строго по ширині; число — явний масштаб.
 */
export type DiskPdfZoom = 'auto' | 'width' | number

export const PDF_ZOOM_MIN = 0.25
export const PDF_ZOOM_MAX = 4
const PDF_ZOOM_STEP = 1.2
export const PDF_AUTO_MAX = 1.25
/** Поля довкола сторінки в режимі «по ширині», px. */
const FIT_GUTTER = 32

interface PageSize {
    width: number
    height: number
}

const clamp = (scale: number): number => Math.min(PDF_ZOOM_MAX, Math.max(PDF_ZOOM_MIN, scale))

/** 403 від сервера: підписане посилання прострочене або право забрали. */
const isForbidden = (error: unknown): boolean => (error as { status?: unknown } | null)?.status === 403

/**
 * Відкритий PDF у переглядачі: документ pdf.js, масштаб, поточна сторінка й
 * пошук по тексту. Один екземпляр на показ файлу (`DiskPdfView`); малюють
 * сторінки компоненти — ліниво, коли сторінка підходить до видимої області.
 */
export class DiskPdfStore {
    doc: PDFDocumentProxy | null = null
    pageCount = 0
    /** Розмір першої сторінки за масштабу 1 — для заглушок ще не намальованих сторінок. */
    pageSize: PageSize | null = null
    isLoading = true
    error: string | null = null

    zoom: DiskPdfZoom = 'auto'
    containerWidth = 0
    currentPage = 1

    searchQuery = ''
    /** Сторінки зі збігами, за зростанням. */
    matches: number[] = []
    matchIndex = -1
    isSearching = false
    /** Пошук за поточним запитом уже пройшов (щоб відрізнити «не знайдено» від «ще не шукали»). */
    searchDone = false

    private loadingDoc: PDFDocumentLoadingTask | null = null
    private readonly pageTexts = new Map<number, string>()
    private searchId = 0
    private loadRun = 0

    constructor() {
        makeAutoObservable<DiskPdfStore, 'loadingDoc' | 'pageTexts' | 'searchId' | 'loadRun'>(this, {
            doc: observable.ref,
            loadingDoc: false,
            pageTexts: false,
            searchId: false,
            loadRun: false,
        })
    }

    get scale(): number {
        if (typeof this.zoom === 'number') return this.zoom
        if (this.pageSize === null || this.containerWidth <= 0) return 1

        const width = clamp((this.containerWidth - FIT_GUTTER) / this.pageSize.width)

        return this.zoom === 'auto' ? Math.min(width, PDF_AUTO_MAX) : width
    }

    /**
     * Відкрити документ за підписаним посиланням. `onForbidden` — посилання
     * відхилено (403): переглядач візьме нове й передасть його знову.
     *
     * `disableRange`: шматкові запити (Range) зараз не доходять до PHP через
     * `fastcgi_cache` ядрового nginx — сервер відповідав би цілим файлом на
     * кожен шматок. Тому PDF читається одним потоком (`disableStream` лишаємо
     * вимкненим — перші сторінки малюються, поки решта ще йде).
     */
    async load(url: string, onForbidden: () => void): Promise<void> {
        this.destroy()
        this.isLoading = true
        this.error = null

        const run = ++this.loadRun

        try {
            const pdfjs = await loadPdfJs()

            if (run !== this.loadRun) return

            const loadingDoc = pdfjs.getDocument({ url, withCredentials: false, disableRange: true })

            this.loadingDoc = loadingDoc

            const doc = await loadingDoc.promise
            const first = await doc.getPage(1)
            const viewport = first.getViewport({ scale: 1 })

            runInAction(() => {
                if (run !== this.loadRun) return
                this.doc = doc
                this.pageCount = doc.numPages
                this.pageSize = { width: viewport.width, height: viewport.height }
                this.currentPage = 1
            })
        } catch (error) {
            // Показ уже змінився (новий файл, нове посилання, переглядач закрили) — це не помилка
            if (run !== this.loadRun) return

            if (isForbidden(error)) {
                onForbidden()

                return
            }

            runInAction(() => {
                this.error =
                    (error as { name?: unknown } | null)?.name === 'PasswordException'
                        ? 'PDF захищено паролем — перегляд у браузері недоступний. Скачайте файл.'
                        : 'Не вдалося відкрити PDF. Можливо, файл пошкоджений — спробуйте скачати його.'
            })
        } finally {
            runInAction(() => {
                if (run === this.loadRun) this.isLoading = false
            })
        }
    }

    destroy(): void {
        const loadingDoc = this.loadingDoc

        this.loadRun += 1
        this.loadingDoc = null
        this.doc = null
        this.pageCount = 0
        this.pageSize = null
        this.pageTexts.clear()
        this.searchId += 1
        this.matches = []
        this.matchIndex = -1
        this.searchDone = false
        if (loadingDoc) void loadingDoc.destroy()
    }

    setContainerWidth(width: number): void {
        if (Math.abs(width - this.containerWidth) >= 1) this.containerWidth = width
    }

    setCurrentPage(page: number): void {
        this.currentPage = page
    }

    zoomIn(): void {
        this.zoom = clamp(this.scale * PDF_ZOOM_STEP)
    }

    zoomOut(): void {
        this.zoom = clamp(this.scale / PDF_ZOOM_STEP)
    }

    fitWidth(): void {
        this.zoom = 'width'
    }

    // ---- Пошук -------------------------------------------------------------

    setSearchQuery(query: string): void {
        this.searchQuery = query
        this.searchId += 1
        this.matches = []
        this.matchIndex = -1
        this.isSearching = false
        this.searchDone = false
    }

    /** Текст сторінки (кешується) — для пошуку. */
    private async pageText(page: number): Promise<string> {
        const cached = this.pageTexts.get(page)
        const doc = this.doc

        if (cached !== undefined) return cached
        if (doc === null) return ''

        const content = await (await doc.getPage(page)).getTextContent()
        const text = content.items
            .map((item) => ('str' in item ? item.str : ''))
            .join(' ')
            .toLocaleLowerCase('uk')

        this.pageTexts.set(page, text)

        return text
    }

    /**
     * Знайти сторінки, де є запит (без урахування регістру), і стати на
     * наступну після поточної. Повторний виклик з тим самим запитом — до
     * наступного збігу по колу. Повертає сторінку, на яку перейти, або `null`.
     */
    async findNext(): Promise<number | null> {
        const query = this.searchQuery.trim().toLocaleLowerCase('uk')

        if (query === '' || this.doc === null) return null

        if (!this.searchDone && !this.isSearching) {
            const id = ++this.searchId
            const found: number[] = []

            this.isSearching = true

            for (let page = 1; page <= this.pageCount; page += 1) {
                const text = await this.pageText(page)

                if (id !== this.searchId) return null
                if (text.includes(query)) found.push(page)
            }

            runInAction(() => {
                this.matches = found
                this.isSearching = false
                this.searchDone = true
                const after = found.findIndex((page) => page >= this.currentPage)
                this.matchIndex = found.length === 0 ? -1 : Math.max(0, after)
            })

            return this.matches[this.matchIndex] ?? null
        }

        if (this.matches.length === 0) return null

        this.matchIndex = (this.matchIndex + 1) % this.matches.length

        return this.matches[this.matchIndex] ?? null
    }
}
